import React, { useState, useEffect, useMemo } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { supabase } from '../supabase/supabaseClient'
import { convertOdds } from '../utils/oddsConverter'
import { BOOKMAKER_LOGOS, ALL_BOOKMAKERS } from '../config/bookmakers'
import { ALL_SPORTS } from '../config/sports'
import { MARKET_TYPE_FILTER_MAPPING } from '../config/markets'
import { saveUserFilter, getUserFilters, deleteUserFilter, setDefaultFilter, unsetDefaultFilter, getDefaultFilter } from '../services/userDataService'
import FilterPanel, { DEFAULT_FILTERS } from './FilterPanel'
import HiddenBetsModal, { useHiddenBets } from './HiddenBetsModal'
import TrackBetModal, { useTrackBet } from './TrackBetModal'
import EVMultiBuilder from './EVMultiBuilder'
import { settlementFields } from '../TRACKER_PAGE/settlement'
import ShareModal from './ShareModal'
import EVTour from './evTour'
import EVCard from './EVCard'
import PreviewWall from '../components/PreviewWall'
import '../styles/app.css'
import '../styles/app-mobile.css'
import './EVNew.css'
import useVisiblePolling from '../hooks/useVisiblePolling'

// Saved-filter format kept compatible with legacy EV8 records (same as desktop EVNew):
//   filters.sports               = whitelist of selected leagues
//   filters.bookmakers           = whitelist of selected bookies
//   filters.excludedMarketTypes  = blacklist of market display names
const PAGE_TYPE = 'ev'

// ─── Helpers ─────────────────────────────────────────────────────────────────

// Extract the short side label from a selection. "Texas Rangers Over 0.5 Total Runs" → "Over 0.5"
// Falls back to the original string for moneyline/team-name selections.
const shortSideLabel = (selection) => {
  if (!selection) return ''
  const m = selection.match(/(over|under)\s+(-?\d+(?:\.\d+)?)/i)
  if (m) return `${m[1].charAt(0).toUpperCase()}${m[1].slice(1).toLowerCase()} ${m[2]}`
  return selection
}

const getHoursUntilGame = (ts) => {
  if (!ts) return 0
  const diff = new Date(ts + 'Z') - new Date()
  return Math.max(0, diff / (1000 * 60 * 60))
}

const formatTimeWithSeconds = (dateString) => {
  if (!dateString) return ''
  return new Date(dateString + 'Z').toLocaleTimeString('en-US', {
    hour: 'numeric', minute: '2-digit', second: '2-digit', hour12: true,
  })
}

// ─── Component ───────────────────────────────────────────────────────────────

export default function EVNewMobile({ isVisible = true, isPreview = false }) {
  const { user, oddsFormat, hasAccess, hadTrial, loading: authLoading } = useAuth()
  isPreview = isPreview || authLoading || !hasAccess('ev')

  const [opportunities,         setOpportunities]         = useState([])
  const [filteredOpportunities, setFilteredOpportunities] = useState([])
  const [loading,               setLoading]               = useState(true)
  const [error,                 setError]                 = useState(null)
  const [autoRefreshEnabled,    setAutoRefreshEnabled]    = useState(false)
  const { hiddenBets, hideOpp, unhideOpp, clearAll, isHidden, showModal, setShowModal } = useHiddenBets()
  const [showFilterPanel,       setShowFilterPanel]       = useState(false)
  const [expandedCard,          setExpandedCard]          = useState(null)
  const [shareBet,              setShareBet]              = useState(null)
  const [sortBy,                setSortBy]                = useState('ev_percentage')
  const [sortDir,               setSortDir]               = useState('desc')
  const [evMode,                setEvMode]                = useState('pct') // 'pct' | 'dollar'
  const [currentPage,           setCurrentPage]           = useState(1)
  const itemsPerPage = 25

  const [filters,       setFilters]       = useState(() => ({
    excludedBookmakers:  new Set(),
    excludedMarketTypes: new Set(),
    excludedSports:      new Set(),
    odds:       DEFAULT_FILTERS.odds,
    ev:         DEFAULT_FILTERS.ev,
    hoursUntil: DEFAULT_FILTERS.hoursUntil,
    minBooks:   DEFAULT_FILTERS.minBooks,
  }))
  const [kellyFraction, setKellyFraction] = useState('1/2')
  const [bankroll,      setBankroll]      = useState(1000)
  const [roundStakes,   setRoundStakes]   = useState('5')

  const { isTracked, openTrackModal, closeTrackModal, saveTrack, showTrackModal, trackingOpp, trackForm, setTrackForm } =
    useTrackBet({ kellyFraction, bankroll, roundStakes, hideOpp })

  const [savedFilters,        setSavedFilters]        = useState([])
  const [activeFilterId,      setActiveFilterId]      = useState(null)
  const [filterName,          setFilterName]          = useState('')
  const [defaultFilterLoaded, setDefaultFilterLoaded] = useState(false)

  // Multi builder
  const [showMultiModal, setShowMultiModal] = useState(false)
  const [multiLegs,      setMultiLegs]      = useState([])
  const [pickerState,    setPickerState]    = useState(null)
  const [multiError,     setMultiError]     = useState(null)

  // Map of display-name market types → set of raw API market types they cover.
  const excludedRawMarketTypes = useMemo(() => {
    const out = new Set()
    filters.excludedMarketTypes.forEach(displayName => {
      const rawTypes = MARKET_TYPE_FILTER_MAPPING[displayName]
      if (rawTypes) rawTypes.forEach(t => out.add(t))
    })
    return out
  }, [filters.excludedMarketTypes])

  // ─── Derived ───────────────────────────────────────────────────────────────

  // Group rows that share match + market + selection + odds + EV — multiple bookmakers
  // offering the same line collapse into one card with logos side-by-side.
  const groupedOpportunities = useMemo(() => {
    const groups = new Map()
    filteredOpportunities.forEach(opp => {
      const key = `${opp.match}_${opp.market_type}_${opp.selection}_${opp.original_odds}_${typeof opp.ev_percentage === 'number' ? opp.ev_percentage.toFixed(2) : ''}`
      if (groups.has(key)) {
        groups.get(key).bookmakers.push({ name: opp.bookmaker, game_url: opp.game_url })
      } else {
        groups.set(key, {
          ...opp,
          bookmakers: [{ name: opp.bookmaker, game_url: opp.game_url }],
          groupKey: key,
        })
      }
    })
    return [...groups.values()]
  }, [filteredOpportunities])

  // ─── Stake ─────────────────────────────────────────────────────────────────

  const computeStakeAmount = (kellyStake) => {
    if (typeof kellyStake !== 'number') return null
    const fractions = { '1/4': 0.25, '1/2': 0.5, '3/4': 0.75, 'Full': 1 }
    const frac = fractions[kellyFraction] ?? 0.5
    const stake = (kellyStake / 100) * frac * bankroll
    if (roundStakes === '1') return Math.round(stake)
    if (roundStakes === '5') return Math.round(stake / 5) * 5
    return Math.round(stake * 100) / 100
  }

  const computeStake = (kellyStake) => {
    const amt = computeStakeAmount(kellyStake)
    return amt == null ? '' : `$${amt}`
  }

  // Numeric dollar EV = displayed stake × EV%.
  const dollarEvAmount = (opp) => {
    const stake = computeStakeAmount(opp.kelly_stake)
    if (stake == null || typeof opp.ev_percentage !== 'number') return null
    return stake * (opp.ev_percentage / 100)
  }
  const computeDollarEv = (opp) => {
    const ev = dollarEvAmount(opp)
    if (ev == null) return ''
    return `${ev >= 0 ? '+' : '−'}$${Math.abs(ev).toFixed(2)}`
  }

  // EV badge color. In $ mode, scale by bankroll (matches desktop bands).
  const evBadgeClass = (opp) => {
    if (evMode === 'dollar') {
      const ev = dollarEvAmount(opp)
      if (ev == null || !bankroll) return 'bc-ev-badge bc-ev-low'
      const pctOfRoll = (ev / bankroll) * 100
      if (pctOfRoll >= 0.5)  return 'bc-ev-badge bc-ev-high'
      if (pctOfRoll >= 0.24) return 'bc-ev-badge bc-ev-mid'
      return 'bc-ev-badge bc-ev-low'
    }
    const ev = opp.ev_percentage
    if (ev >= 10) return 'bc-ev-badge bc-ev-high'
    if (ev >= 5)  return 'bc-ev-badge bc-ev-mid'
    return 'bc-ev-badge bc-ev-low'
  }

  // ─── Multi Builder helpers ────────────────────────────────────────────────

  const multiLegKey = (opp) =>
    `${opp.selection}|${opp.market_type}|${opp.commence_time}|${opp.original_odds}`

  const isInMulti  = (opp) => multiLegs.some(l => l._key === multiLegKey(opp))
  const lockedBook = multiLegs.length > 0 ? multiLegs[0].bookmaker : null

  const addMultiLeg = (opp, bookmakerName) => {
    const bm = (opp.bookmakers || []).find(b => b.name === bookmakerName)
    setMultiLegs(prev => [...prev, {
      _key:          multiLegKey(opp),
      selection:     opp.selection,
      match:         opp.match,
      market_type:   opp.market_type,
      sport:         opp.sport,
      commence_time: opp.commence_time,
      bookmaker:     bookmakerName,
      game_url:      bm?.game_url || null,
      odds:          opp.original_odds,
      ev_percentage: opp.ev_percentage,
      fair_odds:     opp.fair_odds,
      // Teams and the raw values behind the selection, so a multi's legs can be
      // settled against results without parsing the display string back apart.
      ...settlementFields(opp),
    }])
  }

  const flashMultiError = (key, msg) => {
    setMultiError({ key, msg })
    setTimeout(() => setMultiError(prev => (prev?.key === key ? null : prev)), 2800)
  }

  const handleMultiLegClick = (opp) => {
    const key = multiLegKey(opp)

    if (isInMulti(opp)) {
      setMultiLegs(prev => prev.filter(l => l._key !== key))
      return
    }

    // Correlation: same selection + base market + game already in multi
    const baseMarket = (opp.market_type || '').replace('_milestones', '')
    const correlated = multiLegs.some(l =>
      l.commence_time === opp.commence_time &&
      l.selection === opp.selection &&
      (l.market_type || '').replace('_milestones', '') === baseMarket
    )
    if (correlated) {
      flashMultiError(key, '⚠ Already in multi (correlated)')
      return
    }

    if (multiLegs.length === 0) {
      if ((opp.bookmakers || []).length === 1) {
        addMultiLeg(opp, opp.bookmakers[0].name)
      } else {
        setPickerState({ opp })
      }
      return
    }

    if (!(opp.bookmakers || []).some(b => b.name === lockedBook)) {
      flashMultiError(key, `Not on ${lockedBook}`)
      return
    }

    addMultiLeg(opp, lockedBook)
  }

  // ─── Fetch ─────────────────────────────────────────────────────────────────

  const fetchOpportunities = async (silent = false) => {
    try {
      if (!silent) setLoading(true)
      const { data, error } = await supabase.functions.invoke('ev-proxy')
      if (error) throw error
      setOpportunities(data.opportunities || [])
      setError(null)
    } catch (err) {
      setError(err.message)
    } finally {
      if (!silent) setLoading(false)
    }
  }

  // ─── Effects ───────────────────────────────────────────────────────────────

  useEffect(() => { fetchOpportunities() }, [])

  // Load saved filters list (whitelist sports/bookmakers + blacklist markets — same shape as desktop)
  useEffect(() => {
    if (!user?.id) return
    getUserFilters(user.id, PAGE_TYPE).then(({ data }) => {
      if (data) setSavedFilters(data.map(f => ({
        id: f.id,
        name: f.filter_name,
        isDefault: f.is_default || false,
        ...f.filter_data,
      })))
    })
  }, [user?.id])

  useEffect(() => {
    if (!user?.id || defaultFilterLoaded) return
    getDefaultFilter(user.id, PAGE_TYPE).then(({ data }) => {
      if (data) applyFilterData(data.filter_data)
      setDefaultFilterLoaded(true)
    })
  }, [user?.id, defaultFilterLoaded])

  useVisiblePolling(() => fetchOpportunities(true), 30000, autoRefreshEnabled && isVisible)

  // Filter + sort. Empty excludedSports = "all sports", same for bookmakers.
  useEffect(() => {
    const filtered = opportunities.filter(opp => {
      if (filters.excludedSports.has(opp.sport)) return false
      if (filters.excludedBookmakers.has(opp.bookmaker)) return false
      if (excludedRawMarketTypes.has(opp.market_type)) return false
      if (typeof opp.original_odds === 'number' &&
          (opp.original_odds < filters.odds[0] || opp.original_odds > filters.odds[1])) return false
      if (typeof opp.ev_percentage === 'number' &&
          (opp.ev_percentage < filters.ev[0] || opp.ev_percentage > filters.ev[1])) return false
      const hours = getHoursUntilGame(opp.commence_time)
      if (hours < filters.hoursUntil[0] || hours > filters.hoursUntil[1]) return false
      if (typeof opp.bookmaker_count === 'number' && opp.bookmaker_count < filters.minBooks) return false
      return true
    })

    const sorted = [...filtered].sort((a, b) => {
      let av, bv
      if (sortBy === 'ev_percentage') {
        if (evMode === 'dollar') {
          const d = (o) => { const e = dollarEvAmount(o); return e == null ? -Infinity : e }
          av = d(a); bv = d(b)
        } else { av = a.ev_percentage; bv = b.ev_percentage }
      }
      else if (sortBy === 'original_odds') { av = a.original_odds;  bv = b.original_odds }
      else if (sortBy === 'kelly_stake')   { av = a.kelly_stake;    bv = b.kelly_stake }
      else if (sortBy === 'commence_time') {
        av = new Date(a.commence_time + 'Z').getTime()
        bv = new Date(b.commence_time + 'Z').getTime()
      }
      else if (sortBy === 'sport') {
        return sortDir === 'desc' ? b.sport.localeCompare(a.sport) : a.sport.localeCompare(b.sport)
      }
      else { av = 0; bv = 0 }
      return sortDir === 'desc' ? bv - av : av - bv
    })

    setFilteredOpportunities(sorted)
    setCurrentPage(1)
    // dollarEvAmount is an inline fn (new identity each render); its inputs are already in deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opportunities, filters, excludedRawMarketTypes, sortBy, sortDir, evMode, kellyFraction, bankroll, roundStakes])

  // Tick every 10s so the live-data dot can re-evaluate freshness without depending on a refetch.
  // eslint-disable-next-line no-unused-vars
  const [tick, setTick] = useState(0)
  useEffect(() => {
    const id = setInterval(() => setTick(t => t + 1), 10000)
    return () => clearInterval(id)
  }, [])

  // ─── Saved filters (whitelist sports/bookies, blacklist markets — EV8-compat) ──

  const applyFilterData = (fd) => {
    if (!fd?.filters) return
    // Prefer the saved excludedSports blacklist (newer filters) so leagues added after the
    // filter was saved stay visible. Fall back to the legacy sports whitelist for old filters.
    const excludedSports = Array.isArray(fd.filters.excludedSports)
      ? fd.filters.excludedSports
      : ((Array.isArray(fd.filters.sports) && fd.filters.sports.length > 0)
          ? ALL_SPORTS.filter(s => !fd.filters.sports.includes(s))
          : [])

    const bookmakersWhitelist = fd.filters.bookmakers
    const excludedBookmakers = (Array.isArray(bookmakersWhitelist) && bookmakersWhitelist.length > 0)
      ? ALL_BOOKMAKERS.filter(b => !bookmakersWhitelist.includes(b))
      : []

    setFilters({
      excludedSports:      new Set(excludedSports),
      excludedBookmakers:  new Set(excludedBookmakers),
      excludedMarketTypes: new Set(fd.filters.excludedMarketTypes || []),
      odds:       fd.filters.odds       || DEFAULT_FILTERS.odds,
      ev:         fd.filters.ev         || DEFAULT_FILTERS.ev,
      hoursUntil: fd.filters.hoursUntil || DEFAULT_FILTERS.hoursUntil,
      minBooks:   fd.filters.minBooks   ?? DEFAULT_FILTERS.minBooks,
    })
    if (fd.stakeSettings) {
      setKellyFraction(fd.stakeSettings.kellyFraction)
      setBankroll(fd.stakeSettings.bankroll)
      setRoundStakes(fd.stakeSettings.roundStakes)
    }
  }

  const handleSaveFilter = async (name) => {
    if (!name.trim() || !user?.id) return
    const sportsWhitelist     = ALL_SPORTS.filter(s => !filters.excludedSports.has(s))
    const bookmakersWhitelist = ALL_BOOKMAKERS.filter(b => !filters.excludedBookmakers.has(b))

    const fd = {
      filters: {
        sports:              sportsWhitelist,                  // legacy whitelist (kept for back-compat)
        excludedSports:      [...filters.excludedSports],      // blacklist = source of truth on load
        bookmakers:          bookmakersWhitelist,
        excludedMarketTypes: [...filters.excludedMarketTypes],
        odds: filters.odds,
        ev: filters.ev,
        hoursUntil: filters.hoursUntil,
        minBooks: filters.minBooks,
      },
      stakeSettings: { kellyFraction, bankroll, roundStakes },
      createdAt: new Date().toISOString(),
    }
    const { data, error } = await saveUserFilter(user.id, PAGE_TYPE, name.trim(), fd)
    if (error) { alert('Error saving filter: ' + error); return }
    if (data) {
      setSavedFilters(prev => [...prev, {
        id: data.id,
        name: data.filter_name,
        isDefault: false,
        ...data.filter_data,
      }])
    }
    setFilterName('')
  }

  const handleLoadFilter = (saved) => { applyFilterData(saved); setActiveFilterId(saved.id) }

  const handleDeleteFilter = async (id) => {
    if (!user?.id) return
    await deleteUserFilter(id)
    setSavedFilters(prev => prev.filter(f => f.id !== id))
    if (activeFilterId === id) setActiveFilterId(null)
  }

  const handleToggleDefault = async (id) => {
    if (!user?.id) return
    const current = savedFilters.find(f => f.id === id)
    if (!current) return
    if (current.isDefault) {
      await unsetDefaultFilter(user.id, PAGE_TYPE, id)
    } else {
      await setDefaultFilter(user.id, PAGE_TYPE, id)
    }
    setSavedFilters(prev => prev.map(f => ({ ...f, isDefault: f.id === id ? !current.isDefault : false })))
  }

  // ─── Pagination ────────────────────────────────────────────────────────────

  const visibleOpps = groupedOpportunities.filter(o => !isHidden(o))
  const totalPages  = Math.max(1, Math.ceil(visibleOpps.length / itemsPerPage))
  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(1)
  }, [totalPages, currentPage])
  const pagedOpps = isPreview
    ? visibleOpps.slice(0, 4)
    : visibleOpps.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)

  if (!isVisible) return null

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="bcm-container">

      {/* Header */}
      <div className="bcm-header">

        <div className="bcm-action-row">

          {/* Left group: Filters, Multi, Hidden */}
          <div className="bcm-action-left">
            <button className="bc-btn" onClick={() => setShowFilterPanel(true)}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/>
              </svg>
              Filters
            </button>

            {!isPreview && (
              <button
                className={`bc-btn bc-btn-toggle${multiLegs.length > 0 ? ' toggle-active' : ''}`}
                onClick={() => setShowMultiModal(true)}
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/>
                  <rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/>
                </svg>
                {multiLegs.length > 0 && <span className="bc-multi-count">{multiLegs.length}</span>}
              </button>
            )}

            {Object.keys(hiddenBets).length > 0 && (
              <button className="bc-btn" onClick={() => setShowModal(true)}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
                  <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
                  <line x1="1" y1="1" x2="23" y2="23"/>
                </svg>
                {Object.keys(hiddenBets).length}
              </button>
            )}
          </div>

          {/* Right group: Auto refresh + Manual refresh */}
          <div className="bcm-action-right">
            <button
              className={`bc-btn bc-btn-toggle${autoRefreshEnabled ? ' toggle-active' : ''}`}
              onClick={() => setAutoRefreshEnabled(v => !v)}
            >
              Auto
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/>
                <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
              </svg>
            </button>
            <button className="bc-btn bc-icon-btn" onClick={() => fetchOpportunities()} disabled={loading}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className={loading ? 'bc-spin' : ''}>
                <polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/>
                <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
              </svg>
            </button>
          </div>

        </div>
      </div>

      {/* Error */}
      {error && <div className="bc-error" style={{ margin: '12px' }}>Error: {error}</div>}

      {/* Loading */}
      {loading && (
        <div className="bc-loading">
          <div className="bc-spinner" />
        </div>
      )}

      {/* Cards */}
      {!loading && (
        <div className="bcm-cards">
          {pagedOpps.map((opp, idx) => {
            const blurCard  = isPreview && idx > 0
            const blurStyle = blurCard ? { filter: 'blur(5px)', userSelect: 'none', pointerEvents: 'none' } : {}
            const inMulti  = isInMulti(opp)
            const legIdx   = multiLegs.findIndex(l => l._key === multiLegKey(opp))
            const cardKey  = multiLegKey(opp)
            const isExpanded = expandedCard === cardKey

            // EV's API returns single-side odds (no under_odds). When the API gains a
            // per-bookmaker `under_odds` field, this can become a two-column grid like
            // PROP_EV's. See note in EVNew.jsx.
            const bookEntries = Object.entries(opp.all_bookmaker_odds || {})
              .sort((a, b) => (b[1].odds || 0) - (a[1].odds || 0))
            const hasOpposing  = bookEntries.some(([, d]) => typeof d.opposing_odds === 'number')
            // Milestones (Over-only): the picked book is intentionally excluded from the grid,
            // so don't flag any paired book as "best" on the PICKED side — it would imply a
            // paired book beats the pick. The opposing (Under) side is a genuine comparison
            // set, so its best-highlight stays.
            const isMilestone  = opp.is_milestone || (opp.market_type || '').endsWith('_milestones')
            const topOdds      = bookEntries[0]?.[1].odds
            const bestSet      = isMilestone
              ? new Set()
              : new Set(bookEntries.filter(([, d]) => d.odds === topOdds).map(([b]) => b))
            const topOpposing  = hasOpposing ? Math.max(...bookEntries.map(([, d]) => d.opposing_odds || 0)) : null
            const bestOpposing = hasOpposing
              ? new Set(bookEntries.filter(([, d]) => d.opposing_odds === topOpposing).map(([b]) => b))
              : new Set()

            return (
              <React.Fragment key={cardKey}>
              <EVCard
                opp={opp}
                style={blurStyle}
                blurPick={isPreview && idx === 0}
                evText={evMode === 'dollar' ? computeDollarEv(opp) : `+${opp.ev_percentage.toFixed(2)}%`}
                evClass={evBadgeClass(opp)}
                oddsText={convertOdds ? convertOdds(opp.original_odds, oddsFormat) : opp.original_odds?.toFixed(2)}
                stakeText={computeStake(opp.kelly_stake)}
                inMulti={inMulti}
                legIdx={legIdx}
                isExpanded={isExpanded}
                tracked={isTracked(opp)}
                showButtons={!isPreview}
                onToggleEv={() => setEvMode(m => m === 'pct' ? 'dollar' : 'pct')}
                onOpenBook={(b) => !isPreview && b.game_url && window.open(b.game_url, '_blank')}
                onMulti={() => handleMultiLegClick(opp)}
                onExpand={() => setExpandedCard(isExpanded ? null : cardKey)}
                onTrack={() => !isTracked(opp) && openTrackModal(opp)}
                onShare={() => setShareBet(opp)}
                onHide={() => hideOpp(opp)}
              >
                {/* Expanded section: bookmaker odds + stats */}
                {isExpanded && (
                  <div className="bcm-odds-dropdown">

                    {/* Per-bookmaker odds */}
                    {bookEntries.length > 0 && (
                      <>
                        {hasOpposing && (() => {
                          const oppEntry = bookEntries.find(([, d]) => d.opposing_selection)
                          const pickedLabel = shortSideLabel(opp.selection)
                          const pairedLabel = oppEntry ? shortSideLabel(oppEntry[1].opposing_selection) : 'Paired'
                          return (
                            <div className="bcm-odds-header">
                              <span className="bcm-odds-label">{pickedLabel}</span>
                              <span />
                              <span className="bcm-odds-label">{pairedLabel}</span>
                            </div>
                          )
                        })()}
                        {bookEntries.map(([bName, data]) => {
                          const isBest = bestSet.has(bName)
                          return (
                            <div key={bName} className={`bcm-odds-row${isBest ? ' best-row' : ''}`} title={typeof data.margin === 'number' ? `${data.margin.toFixed(2)}% margin` : undefined}>
                              <span className={`bcm-odds-val${isBest ? ' is-best' : ''}`}>
                                {convertOdds ? convertOdds(data.odds, oddsFormat) : data.odds?.toFixed(2)}
                              </span>
                              {BOOKMAKER_LOGOS[bName]
                                ? <img src={BOOKMAKER_LOGOS[bName]} alt={bName} className={`bcm-odds-logo${isBest ? ' is-best-book' : ''}`} />
                                : <span className={`bcm-odds-bname${isBest ? ' is-best-book' : ''}`}>{bName}</span>}
                              {hasOpposing && (
                                <span className={`bcm-odds-val${bestOpposing.has(bName) ? ' is-best' : ''}`}>
                                  {typeof data.opposing_odds === 'number'
                                    ? (convertOdds ? convertOdds(data.opposing_odds, oddsFormat) : data.opposing_odds.toFixed(2))
                                    : '—'}
                                </span>
                              )}
                            </div>
                          )
                        })}
                      </>
                    )}

                    {/* Stats */}
                    <div className="bcm-stats-grid">
                      {typeof opp.fair_odds === 'number' && (
                        <div className="bcm-stat">
                          <span className="bcm-stat-label">Fair Odds</span>
                          <span className="bcm-stat-val">{opp.fair_odds.toFixed(2)}</span>
                        </div>
                      )}
                      {typeof opp.win_probability === 'number' && (
                        <div className="bcm-stat">
                          <span className="bcm-stat-label">Win Prob</span>
                          <span className="bcm-stat-val">{opp.win_probability.toFixed(2)}%</span>
                        </div>
                      )}
                      {opp.last_update && (
                        <div className="bcm-stat">
                          <span className="bcm-stat-label">Updated</span>
                          <span className="bcm-stat-val">{formatTimeWithSeconds(opp.last_update)}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Correlated / bookmaker error tip */}
                {multiError?.key === multiLegKey(opp) && (
                  <div className="bcm-err-tip">{multiError.msg}</div>
                )}
              </EVCard>

              {/* Preview wall — right below the first (teaser) card */}
              {isPreview && idx === 0 && (
                <PreviewWall
                  title={`${groupedOpportunities.length} +EV opportunities right now`}
                  features={['Real time odds from all major bookmakers', 'Direct links to each bookmakers game page', 'Automatic multi builder', 'Much more...']}
                  planName="Sports Premium"
                  accent="#60a5fa"
                  hadTrial={hadTrial}
                />
              )}
              </React.Fragment>
            )
          })}

          {/* Preview wall fallback — still shows when there are zero opportunities */}
          {isPreview && pagedOpps.length === 0 && (
            <PreviewWall
              title="+EV opportunities right now"
              features={['Real time odds from all major bookmakers', 'Direct links to each bookmakers game page', 'Automatic multi builder', 'Much more...']}
              planName="Sports Premium"
              accent="#60a5fa"
              hadTrial={hadTrial}
            />
          )}

          {/* Empty */}
          {!isPreview && visibleOpps.length === 0 && (
            <div className="bc-empty">No opportunities match your filters.</div>
          )}
        </div>
      )}

      {/* Pagination */}
      {!isPreview && !loading && totalPages > 1 && (
        <div className="bcm-pagination">
          <button
            className="bc-page-arrow"
            onClick={() => { setCurrentPage(1); window.scrollTo({ top: 0 }) }}
            disabled={currentPage === 1}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="11 18 5 12 11 6"/><polyline points="19 18 13 12 19 6"/></svg>
          </button>
          <button
            className="bc-page-arrow"
            onClick={() => { setCurrentPage(p => Math.max(1, p - 1)); window.scrollTo({ top: 0 }) }}
            disabled={currentPage === 1}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
          </button>
          <span className="bc-page-info">{currentPage} / {totalPages}</span>
          <button
            className="bc-page-arrow"
            onClick={() => { setCurrentPage(p => Math.min(totalPages, p + 1)); window.scrollTo({ top: 0 }) }}
            disabled={currentPage === totalPages}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
          </button>
          <button
            className="bc-page-arrow"
            onClick={() => { setCurrentPage(totalPages); window.scrollTo({ top: 0 }) }}
            disabled={currentPage === totalPages}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="5 18 11 12 5 6"/><polyline points="13 18 19 12 13 6"/></svg>
          </button>
        </div>
      )}

      {/* Floating multi FAB — appears once user has selected legs */}
      {!isPreview && multiLegs.length > 0 && !showMultiModal && (
        <button
          className="bc-multi-fab bc-multi-fab-mobile"
          onClick={() => setShowMultiModal(true)}
          title="Open Multi Builder"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/>
            <rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/>
          </svg>
          <span className="bc-multi-fab-label">Multi</span>
          <span className="bc-multi-fab-count">{multiLegs.length}</span>
        </button>
      )}

      {/* Modals — all reused from desktop */}
      <HiddenBetsModal
        mobile
        isOpen={showModal}
        hiddenBets={hiddenBets}
        onUnhide={unhideOpp}
        onUnhideAll={clearAll}
        onClose={() => setShowModal(false)}
      />

      <TrackBetModal
        isOpen={showTrackModal}
        opp={trackingOpp}
        form={trackForm}
        onFormChange={setTrackForm}
        onConfirm={() => saveTrack(false)}
        onConfirmAndHide={() => saveTrack(true)}
        onClose={closeTrackModal}
      />

      {/* Bookmaker picker — fixed/centered on mobile */}
      {pickerState && (
        <>
          <div className="bc-picker-backdrop" onClick={() => setPickerState(null)} />
          <div className="bcm-picker">
            <div className="bc-picker-title">Select Bookmaker</div>
            {pickerState.opp.bookmakers.map(b => (
              <button
                key={b.name}
                className="bc-picker-option"
                onClick={() => { addMultiLeg(pickerState.opp, b.name); setPickerState(null) }}
              >
                {BOOKMAKER_LOGOS[b.name]
                  ? <img src={BOOKMAKER_LOGOS[b.name]} alt={b.name} className="bc-picker-logo" />
                  : <span style={{ width: 26 }} />}
                <span className="bc-picker-name">{b.name}</span>
              </button>
            ))}
          </div>
        </>
      )}

      <EVMultiBuilder
        isOpen={showMultiModal}
        onClose={() => setShowMultiModal(false)}
        rawOpportunities={opportunities}
        selectedLegs={multiLegs}
        onLegsChange={setMultiLegs}
        kellyFraction={kellyFraction}
        bankroll={bankroll}
        roundStakes={roundStakes}
        mobile
      />

      <FilterPanel
        isOpen={showFilterPanel}
        onClose={() => setShowFilterPanel(false)}
        filters={filters}
        setFilters={setFilters}
        kellyFraction={kellyFraction}
        setKellyFraction={setKellyFraction}
        bankroll={bankroll}
        setBankroll={setBankroll}
        roundStakes={roundStakes}
        setRoundStakes={setRoundStakes}
        savedFilters={savedFilters}
        activeFilterId={activeFilterId}
        filterName={filterName}
        setFilterName={setFilterName}
        onSaveFilter={handleSaveFilter}
        onLoadFilter={handleLoadFilter}
        onDeleteFilter={handleDeleteFilter}
        onToggleDefault={handleToggleDefault}
        sortBy={sortBy}
        setSortBy={setSortBy}
        sortDir={sortDir}
        setSortDir={setSortDir}
      />

      {/* Share Modal */}
      {shareBet && <ShareModal bet={shareBet} onClose={() => setShareBet(null)} />}

      <EVTour isVisible={isVisible} isMobile />

    </div>
  )
}
